import React from 'react';
import { Dialog, Box, Typography, Button, IconButton, Chip, Divider, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { dinero } from '../../utils/dinero';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const folioDe = (id) => '#' + String(id || '').slice(-6).toUpperCase();
const hh = (n) => String(n).padStart(2, '0');

// "05 mar 2026 - 14:30"
const fmtCorto = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha);
  if (isNaN(d)) return '—';
  return `${hh(d.getDate())} ${MESES_CORTO[d.getMonth()]} ${d.getFullYear()} - ${hh(d.getHours())}:${hh(d.getMinutes())}`;
};

// "9 de marzo de 2026 a las 10:00 hrs"
const fmtLargo = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha);
  if (isNaN(d)) return '—';
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()} a las ${hh(d.getHours())}:${hh(d.getMinutes())} hrs`;
};

const FACT_LABEL = {
  no_solicitada: 'No solicitada',
  solicitada: 'Factura solicitada',
  emitida: 'Facturado'
};

// Fila de la línea de tiempo (icono en círculo + label + valor)
const HitoTiempo = ({ icon: Icon, color, bg, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
    <Box sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon sx={{ fontSize: 18, color }} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.main' }}>{value}</Typography>
    </Box>
  </Box>
);

// Fila de detalles financieros
const FilaFinanciera = ({ label, value, total }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: total ? 1.2 : 0.8 }}>
    <Typography sx={{ fontSize: total ? 15 : 13.5, fontWeight: total ? 800 : 500, color: total ? 'primary.main' : 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: total ? 17 : 14, fontWeight: total ? 900 : 700, color: 'primary.main' }}>
      {value}
    </Typography>
  </Box>
);

const DetalleReservaModal = ({ open, onClose, booking, personaNombre, onReportar, enRetencion = false, puedeReportar = true }) => {
  if (!booking) return null;

  const unidad = booking.tipoCosto
    ? booking.tipoCosto.charAt(0).toUpperCase() + booking.tipoCosto.slice(1)
    : (booking.cotizacion ? 'Proyecto' : 'Servicio');
  const costo = Number(booking.costoServicio) || 0;
  const total = booking.pago?.monto != null ? booking.pago.monto : costo;

  const estadoFactura = booking.factura?.estado || 'no_solicitada';
  // Aprobado y Finalizado usan los timestamps reales; en reservas anteriores a este
  // cambio no existen, así que se aproximan con createdAt / updatedAt para no salir vacíos.
  const aprobado = booking.fechaConfirmacion || booking.createdAt;
  // Finalizado = momento en que se dio click en "finalizar servicio" (fechaCompletado);
  // en reservas viejas cae al updatedAt (última modificación ≈ cuando se completó).
  const finalizado = booking.fechaCompletado || booking.updatedAt;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '22px', overflow: 'hidden' } } }}>
      {/* ---- Header ---- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), px: 2.5, py: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DescriptionOutlinedIcon sx={{ color: 'secondary.main' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'primary.main' }}>Detalle de la reserva</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>ID de Reserva: {folioDe(booking._id)}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main', mb: 1 }}>
          {booking.servicioNombre || 'Servicio'}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Chip label="Finalizado" size="small" sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.2), color: 'primary.main', fontWeight: 700, fontSize: 12 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <PersonOutlineIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 13.5, color: 'primary.main', fontWeight: 600 }}>{personaNombre}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ borderColor: 'sutil', mb: 2.5 }} />

        {/* ---- Línea de tiempo ---- */}
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'primary.main', letterSpacing: 0.5, mb: 2 }}>
          LÍNEA DE TIEMPO
        </Typography>
        <HitoTiempo icon={EventAvailableOutlinedIcon} color="secondary.main" bg={(t) => alpha(t.palette.secondary.main, 0.12)}
          label="Servicio Solicitado" value={fmtCorto(booking.createdAt)} />
        <HitoTiempo icon={CheckCircleOutlineIcon} color="success.oscuro" bg={(t) => alpha(t.palette.success.main, 0.2)}
          label="Servicio Aprobado" value={fmtCorto(aprobado)} />
        <HitoTiempo icon={AccessTimeIcon} color="secondary.main" bg={(t) => alpha(t.palette.secondary.main, 0.12)}
          label="Finalizado" value={fmtLargo(finalizado)} />

        {/* ---- Detalles financieros ---- */}
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'primary.main', letterSpacing: 0.5, mt: 1, mb: 1.5 }}>
          DETALLES FINANCIEROS
        </Typography>
        <Box sx={{ bgcolor: 'grey.50', borderRadius: '14px', p: 2 }}>
          <FilaFinanciera label="Unidad de cobro" value={unidad} />
          <FilaFinanciera label={`Costo por ${unidad.toLowerCase()}`} value={dinero(costo)} />
          <Divider sx={{ borderColor: 'divider', my: 0.5 }} />
          <FilaFinanciera label="Costo Total" value={dinero(total)} total />
          <Divider sx={{ borderColor: 'divider', my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>Estado de facturación</Typography>
            </Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: estadoFactura === 'emitida' ? 'success.oscuro' : 'text.secondary' }}>
              {FACT_LABEL[estadoFactura]}
            </Typography>
          </Box>
        </Box>
        {/* Ventana de retención: el pago sigue en custodia; un reporte lo congela */}
        {enRetencion && (
          <Box sx={{ mt: 2, bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '12px', p: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, color: 'warning.oscuro', fontWeight: 600 }}>
              Tu pago aún está retenido. Si algo salió mal con el servicio, repórtalo ahora:
              se congelará hasta que el equipo lo revise. Después del corte semanal ya no
              podrás abrir un reporte, solo una aclaración.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ---- Footer ---- */}
      <Box sx={{ display: 'flex', gap: 1.5, px: 3, pb: 3 }}>
        {/* El botón de reportar/aclarar solo aparece dentro de la ventana permitida
            (últimos 3 meses); en servicios más viejos ya no se muestra. */}
        {puedeReportar && (
          <Button fullWidth variant="contained" color="error" disableElevation onClick={onReportar}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', py: 1.3 }}>
            {enRetencion ? 'Reportar problema' : 'Reportar / Aclaración'}
          </Button>
        )}
        <Button fullWidth variant="contained" disableElevation onClick={onClose}
          sx={{ bgcolor: 'primary.main', color: 'white', textTransform: 'none', fontWeight: 700, borderRadius: '12px', py: 1.3,
            '&:hover': { bgcolor: 'primary.dark' } }}>
          Cerrar detalle
        </Button>
      </Box>
    </Dialog>
  );
};

export default DetalleReservaModal;
